import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getAgentKnowledge,
  addAgentKnowledge,
  deleteAgentKnowledge,
  askAgent
} from '../services/api';
import { FiMessageCircle, FiPlus, FiTrash2, FiSend, FiLayers, FiX } from 'react-icons/fi';

const AgentAssistant = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [knowledge, setKnowledge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('ask'); // 'ask' | 'train'

  // Train form
  const [trainType, setTrainType] = useState('qa');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [content, setContent] = useState('');
  const [trainSubmitting, setTrainSubmitting] = useState(false);

  // Ask
  const [askInput, setAskInput] = useState('');
  const [askResult, setAskResult] = useState(null);
  const [askLoading, setAskLoading] = useState(false);

  const fetchKnowledge = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAgentKnowledge();
      setKnowledge(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load knowledge');
      setKnowledge([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKnowledge();
  }, []);

  const handleAddKnowledge = async (e) => {
    e.preventDefault();
    setError('');
    setTrainSubmitting(true);
    try {
      if (trainType === 'qa') {
        if (!question.trim() || !answer.trim()) {
          setError('Question and answer are required.');
          return;
        }
        await addAgentKnowledge({ type: 'qa', question: question.trim(), answer: answer.trim() });
        setQuestion('');
        setAnswer('');
      } else {
        if (!content.trim()) {
          setError('Content is required for document.');
          return;
        }
        await addAgentKnowledge({ type: 'document', content: content.trim() });
        setContent('');
      }
      await fetchKnowledge();
    } catch (err) {
      setError(err.message || 'Failed to add knowledge');
    } finally {
      setTrainSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this knowledge entry?')) return;
    setError('');
    try {
      await deleteAgentKnowledge(id);
      await fetchKnowledge();
    } catch (err) {
      setError(err.message || 'Failed to delete');
    }
  };

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!askInput.trim()) return;
    setError('');
    setAskResult(null);
    setAskLoading(true);
    try {
      const data = await askAgent(askInput.trim());
      setAskResult({ question: data.question || askInput.trim(), answer: data.answer });
      setAskInput('');
    } catch (err) {
      setError(err.message || 'Failed to get answer');
      setAskResult({ question: askInput.trim(), answer: null, error: err.message });
    } finally {
      setAskLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            >
              ← Dashboard
            </button>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FiMessageCircle className="text-indigo-600" /> AI Agent
            </h1>
          </div>
          <span className="text-sm text-gray-500">{user?.username || user?.email}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setTab('ask')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${tab === 'ask' ? 'bg-indigo-100 text-indigo-800' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            Ask
          </button>
          <button
            onClick={() => setTab('train')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${tab === 'train' ? 'bg-indigo-100 text-indigo-800' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            Train (Q&A & Documents)
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700"><FiX /></button>
          </div>
        )}

        {tab === 'ask' && (
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">Ask a question. The agent answers using the knowledge you added in Train.</p>
            <form onSubmit={handleAsk} className="flex gap-2">
              <input
                type="text"
                value={askInput}
                onChange={(e) => setAskInput(e.target.value)}
                placeholder="Type your question..."
                className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={askLoading}
              />
              <button
                type="submit"
                disabled={askLoading || !askInput.trim()}
                className="px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                <FiSend /> {askLoading ? '...' : 'Ask'}
              </button>
            </form>
            {askResult && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <p className="text-gray-500 text-sm mb-1">Your question:</p>
                <p className="font-medium text-gray-900 mb-3">{askResult.question}</p>
                <p className="text-gray-500 text-sm mb-1">Answer:</p>
                <div className="text-gray-800 whitespace-pre-wrap">
                  {askResult.answer != null ? askResult.answer : (askResult.error || 'No answer.')}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'train' && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FiLayers /> Add knowledge
              </h2>
              <form onSubmit={handleAddKnowledge} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={trainType}
                    onChange={(e) => setTrainType(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="qa">Question & Answer</option>
                    <option value="document">Document (plain text)</option>
                  </select>
                </div>
                {trainType === 'qa' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                      <input
                        type="text"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="e.g. How do I add a new contract?"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                      <textarea
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        placeholder="e.g. Go to Dashboard, click Add Contract, fill the form and upload a file."
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </>
                )}
                {trainType === 'document' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Paste policy text, FAQs, or any text the agent should use to answer."
                      rows={5}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                )}
                <button
                  type="submit"
                  disabled={trainSubmitting}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <FiPlus /> {trainSubmitting ? 'Adding...' : 'Add'}
                </button>
              </form>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Saved knowledge ({knowledge.length})</h2>
              {loading ? (
                <p className="text-gray-500">Loading...</p>
              ) : knowledge.length === 0 ? (
                <p className="text-gray-500">No entries yet. Add Q&A or documents above.</p>
              ) : (
                <ul className="space-y-3">
                  {knowledge.map((k) => (
                    <li key={k.id} className="border border-gray-200 rounded-lg p-3 flex justify-between items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <span className="text-xs font-medium text-indigo-600 uppercase">{k.type}</span>
                        {k.type === 'qa' ? (
                          <>
                            <p className="text-gray-900 font-medium mt-0.5">{k.question}</p>
                            <p className="text-gray-600 text-sm mt-1">{k.answer}</p>
                          </>
                        ) : (
                          <p className="text-gray-700 text-sm mt-0.5 line-clamp-2">{k.content}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDelete(k.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Remove"
                      >
                        <FiTrash2 />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AgentAssistant;
